/* eslint-disable no-nested-ternary */
import {
  Stack,
  Divider,
  Text,
  IconButton,
  Icon,
  DrawerBody,
  DrawerFooter,
  Switch,
} from '@chakra-ui/react';
import qs from 'query-string';
import { useMutation, useQueryClient } from 'react-query';
import { useMemo, useState } from 'react';
import { useGetSubscriptionCoupons } from 'api/subscriptionCoupons';
import { datePrettier } from 'utils/date';
import { API, APIRoutes } from 'api';
import notify from 'utils/notify';
import LoadingSpinner from 'components/LoadingSpinner';
import ChevronRight from 'components/Icons/ChevronRight';
import { COUPON_STATUS_DICT } from 'pages/SubscriptionCoupons/Form/constants';

const LIMIT = 10;

function OrderHistory({ userId }: { userId: string }) {
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const {
    isFetching,
    data: { data: coupons, totals } = { data: [], totals: 0 },
  } = useGetSubscriptionCoupons(
    `?${qs.stringify({ userId, limit: LIMIT, page })}`,
    !!userId,
  );
  const hasNextPage = useMemo(
    () => page + 1 <= Math.ceil(totals / +LIMIT),
    [page, totals],
  );

  const { mutate: editSubscriptionCoupon, isLoading: isEditing } = useMutation({
    mutationKey: 'subscriptionCouponsMutation',
    mutationFn: ({ _id, ignoreForLoyalty }: Partial<SubscriptionCoupon>) =>
      API.patch(APIRoutes.subscriptionCoupons.byId(_id!), {
        ignoreForLoyalty: !ignoreForLoyalty,
      }),
    onSuccess: () => {
      notify('success', 'Zmiany w kuponie subskrypcji zostały zastosowane');
      queryClient.invalidateQueries('subscriptionCoupons');
    },
    onError: () => {
      notify('error', 'Wystąpił problem podczas edycji kuponu subskrypcji');
    },
  });

  const isLoading = isFetching || isEditing;

  return (
    <>
      <DrawerBody>
        {isLoading ? (
          <LoadingSpinner />
        ) : coupons.length > 0 ? (
          <Stack spacing={4} divider={<Divider />} fontSize="14px">
            {coupons.map(
              ({
                _id,
                eShopOrderNumber,
                salesDocumentNumber,
                externalTransactionsTotal,
                orderDate,
                status,
                ignoreForLoyalty,
              }) => (
                <Stack spacing={2} key={_id}>
                  <Text as="span">
                    Numer zamówienia: {eShopOrderNumber || '-'}
                  </Text>
                  <Text as="span">
                    Numer dokumentu: {salesDocumentNumber || '-'}
                  </Text>
                  <Text as="span">Wartość: {externalTransactionsTotal}</Text>
                  <Text as="span">
                    Data zamówienia: {datePrettier(orderDate)}
                  </Text>
                  <Text as="span">Status: {COUPON_STATUS_DICT[status]}</Text>
                  <Switch
                    defaultChecked={ignoreForLoyalty}
                    onChange={() =>
                      editSubscriptionCoupon({ _id, ignoreForLoyalty })
                    }
                  >
                    <Text as="span">Ignoruj w programie lojalnościowym</Text>
                  </Switch>
                </Stack>
              ),
            )}
          </Stack>
        ) : (
          <Text color="complementary.lightGrey">Brak zamówień</Text>
        )}
      </DrawerBody>
      {coupons.length > 0 && (
        <DrawerFooter justifyContent="center">
          <IconButton
            aria-label="Prev page"
            variant="unstyled"
            isDisabled={page === 1}
            icon={
              <Icon
                as={ChevronRight}
                transform="rotate(180deg)"
                fontSize={24}
              />
            }
            onClick={() => {
              if (page > 1) setPage((currentPage) => currentPage - 1);
            }}
          />
          <IconButton
            aria-label="Next page"
            variant="unstyled"
            isDisabled={!hasNextPage}
            onClick={() => {
              if (hasNextPage) setPage((currentPage) => currentPage + 1);
            }}
            icon={<Icon as={ChevronRight} fontSize={24} />}
          />
        </DrawerFooter>
      )}
    </>
  );
}

export default OrderHistory;
